/**
 * @file useQaSessionLab.js
 * @brief Create/teardown engine for the QA Session Lab (plan 2026-08-12 §3.2).
 *
 * Spins up a disposable, fully-provisioned Q&A session so the moderator console can be
 * exercised in a plain browser. The sequence is four steps and the ORDER matters:
 *
 *   1. POST /auth/register              — the test account (JWT, ids, credentials)
 *   2. POST /test/seed/tenant           — the chosen tier WITH productType=webinar, because a
 *                                         non-webinar plan has no Q&A features at all
 *   3. POST /test/seed/subscription     — overwrite step 2's row. SeedTestTenant calls the
 *                                         PRODUCTION subscription service, which writes
 *                                         Ttl=-1; stopping here leaks a permanent row from a
 *                                         15-minute test account
 *   4. POST /test/session/register-host — the owned session (source + testRunId + hostId)
 *
 * Retry resumes at the FAILED step and replays the byte-equal payload: the owned-session
 * contract hashes every client-controlled field, so a regenerated hostId or run id is a 409
 * rather than a resume, and re-registering would mint a second account.
 *
 * Secrets: the generated password travels in the register REQUEST BODY only. It is never put
 * in a URL, a log, or the launch link — a JWT or password in a URL lands in history, access
 * logs and Referer. The test JWT stays in component memory and is never handed to ZoomClient,
 * which signs in at its own origin.
 */

import { useCallback, useRef, useState } from 'react';
import { getCurrentTraceId } from '../services/appInsightsService';
import { getJwtToken } from '../utils/jwtService';

/** Ordered pipeline; `retry()` restarts from the first step that has not completed. */
const STEPS = ['register', 'tenant', 'subscription', 'session'];

/** Where a launched console lives. Local-first, matching the Lab's default environment. */
const DEFAULT_CONSOLE_ORIGIN = 'http://localhost:3001';

function randomSuffix() {
  return Math.random().toString(36).slice(2, 10);
}

/** Trace id if one can be produced, otherwise null. Never throws. */
function safeTraceId() {
  try {
    return getCurrentTraceId();
  } catch {
    return null;
  }
}

/**
 * Generates a password that satisfies the register endpoint without being guessable.
 * Displayed masked in the UI; never logged or put in a URL.
 */
function generatePassword() {
  return `Qa!${randomSuffix()}${randomSuffix().toUpperCase()}9`;
}

/**
 * @param {object} options
 * @param {string} options.functionsUrl Base Functions URL for the selected environment.
 * @param {string} [options.consoleOrigin] Origin that serves the Q&A console.
 * @returns {object} Lab state and actions.
 */
export function useQaSessionLab({ functionsUrl, consoleOrigin = DEFAULT_CONSOLE_ORIGIN }) {
  const [state, setState] = useState('idle');
  const [failedStep, setFailedStep] = useState(null);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [credentials, setCredentials] = useState({ email: null, password: null });

  // Everything below is COMPONENT MEMORY ONLY: the test JWT, the generated password, and the
  // exact payloads a retry must replay. None of it is persisted, and none reaches ZoomClient.
  const runRef = useRef(null);

  const request = useCallback(async (path, { body, auth } = {}) => {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Trace id is diagnostic metadata, not a precondition. Losing it must degrade the trace,
    // never fail the request — getCurrentTraceId reaches for crypto, which is absent in some
    // environments, and a Lab that cannot create a session because it could not name one is
    // the wrong failure.
    const traceId = safeTraceId();
    if (traceId) {
      headers['X-Trace-Id'] = traceId;
    }

    // Who is calling, and therefore who the backend attributes this to.
    //
    //   'jwt'      — the account this run just provisioned, for calls made AS that user.
    //   'operator' — YOU, the signed-in SuperAdmin, for the seeding calls.
    //
    // 'operator' replaces what used to be a pasted host key. That key was shared by everyone
    // who had it, identified nobody, could not be revoked for one person, and opened every
    // Function-level route on the host — and asking for it in a portal you had already
    // authenticated to was the tell that it was the wrong credential.
    if (auth === 'jwt' && runRef.current?.jwt) {
      headers.Authorization = `Bearer ${runRef.current.jwt}`;
    }
    if (auth === 'operator') {
      const operatorToken = getJwtToken();
      if (operatorToken) {
        headers.Authorization = `Bearer ${operatorToken}`;
      }
    }

    const response = await fetch(`${functionsUrl}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    // Log method/path/status only — never the payload, which carries the password on step 1.
    // eslint-disable-next-line no-console
    console.log(`[QaSessionLab] POST ${path} -> ${response.status}`);

    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const err = new Error(detail?.error || `Request failed (${response.status})`);
      err.status = response.status;
      throw err;
    }

    return response.json().catch(() => ({}));
  }, [functionsUrl]);

  /**
   * Runs the pipeline from the first incomplete step.
   * @param {object} run The in-memory run record.
   */
  const runFrom = useCallback(async (run) => {
    setState('creating');
    setError(null);
    setFailedStep(null);

    for (const step of STEPS) {
      if (run.completed[step]) {
        continue;
      }

      try {
        if (step === 'register') {
          const result = await request('/auth/register', { body: run.payloads.register });
          run.jwt = result.JwtToken || result.jwtToken;
          run.userId = result.User?.UserId || result.user?.userId;
          run.tenantId = result.User?.TenantId || result.user?.tenantId;

          // Steps 2-4 are pinned to the ids step 1 returned, and frozen so a retry replays
          // them byte-for-byte.
          run.payloads.tenant = {
            tenantId: run.tenantId,
            userId: run.userId,
            tier: run.tier,
            // A non-webinar plan has NO Q&A features — without this the Lab hands back a
            // session where Q&A is silently unavailable.
            productType: 'webinar',
            metadata: { source: 'integration-test', testRunId: run.testRunId }
          };
          run.payloads.subscription = {
            tenantId: run.tenantId,
            metadata: { source: 'integration-test', testRunId: run.testRunId }
          };
          run.payloads.session = {
            sessionId: run.sessionId,
            hostId: run.hostId,
            tenantId: run.tenantId,
            enableQna: true,
            qaAnonymousEnabled: true,
            qaModerationEnabled: true,
            qaRepliesEnabled: true,
            qaDownvotesEnabled: true,
            metadata: { source: 'integration-test' },
            testRunId: run.testRunId
          };
        } else if (step === 'tenant') {
          await request('/test/seed/tenant', { body: run.payloads.tenant, auth: 'operator' });
        } else if (step === 'subscription') {
          const result = await request('/test/seed/subscription', {
            body: run.payloads.subscription,
            auth: 'operator'
          });
          run.subscriptionTtl = result?.ttl ?? null;
        } else if (step === 'session') {
          await request('/test/session/register-host', {
            body: run.payloads.session,
            auth: 'operator'
          });
        }

        run.completed[step] = true;
      } catch (err) {
        setFailedStep(step);
        setError(err.message);
        setState('partial-error');
        return;
      }
    }

    setSession({
      sessionId: run.sessionId,
      tenantId: run.tenantId,
      testRunId: run.testRunId,
      subscriptionTtl: run.subscriptionTtl
    });
    setState('ready');
  }, [request]);

  const createSession = useCallback(async ({ tier = 'Pro' } = {}) => {
    const testRunId = `lab-${Date.now()}-${randomSuffix()}`;
    const sessionId = `lab-${randomSuffix()}${randomSuffix()}`.slice(0, 24);
    const email = `qa-lab-${randomSuffix()}@test.qolsync.ai`;
    const password = generatePassword();

    const run = {
      tier,
      testRunId,
      sessionId,
      // Stable for the lifetime of this run — a fresh hostId on retry would change the
      // server-computed request hash and be refused as a different request.
      hostId: `lab-host-${randomSuffix()}`,
      completed: {},
      payloads: {
        register: { email, password, name: 'QA Lab Test User' }
      }
    };

    runRef.current = run;
    setCredentials({ email, password });
    setSession(null);

    await runFrom(run);
  }, [runFrom]);

  const retry = useCallback(async () => {
    if (!runRef.current) {
      return;
    }
    await runFrom(runRef.current);
  }, [runFrom]);

  /**
   * Tears the session down through the owned DELETE. Presents the run id that created it —
   * teardown without the matching id is refused (403) by design.
   */
  const cleanup = useCallback(async () => {
    const run = runRef.current;
    if (!run) {
      return;
    }

    setState('cleaning');

    try {
      const headers = {
        'X-Test-Run-Id': run.testRunId,
      };
      const cleanupTrace = safeTraceId();
      if (cleanupTrace) {
        headers['X-Trace-Id'] = cleanupTrace;
      }
      const operatorToken = getJwtToken();
      if (operatorToken) {
        headers.Authorization = `Bearer ${operatorToken}`;
      }

      const response = await fetch(`${functionsUrl}/test/session/${run.sessionId}`, {
        method: 'DELETE',
        headers
      });

      // eslint-disable-next-line no-console
      console.log(`[QaSessionLab] DELETE /test/session/${run.sessionId} -> ${response.status}`);

      if (!response.ok) {
        setFailedStep('session-delete');
        setError(`Cleanup failed (${response.status}) for session ${run.sessionId}`);
        setState('cleanup-error');
        return;
      }

      runRef.current = null;
      setSession(null);
      setCredentials({ email: null, password: null });
      setState('idle');
    } catch (err) {
      setFailedStep('session-delete');
      setError(err.message);
      setState('cleanup-error');
    }
  }, [functionsUrl]);

  // The PRIMARY proof link: production-shaped, carrying only the session and a
  // non-authoritative intent. No role override, no token, no credential — moderator must come
  // from /api/qa/connect or it proves nothing.
  const launchUrl = session
    ? `${consoleOrigin}/qa/${session.sessionId}?intent=moderate`
    : null;

  const participantUrl = session
    ? `${consoleOrigin}/qa/${session.sessionId}`
    : null;

  return {
    state,
    failedStep,
    error,
    session,
    credentials,
    launchUrl,
    participantUrl,
    createSession,
    retry,
    cleanup
  };
}

export default useQaSessionLab;
